import { makeStyles } from "tss-react/mui"
import { CSSObject } from "tss-react"
import {
  blueGrey,
  deepPurple,
  indigo,
  lightBlue,
  lightGreen,
  orange,
  red,
  teal
} from "../../core/palette"
import { Color, chipClasses, iconClasses } from "@mui/material"
import { ChipDeleteIconPosition } from "./Chip"

type VariantColor =
  | "bg"
  | "text"
  | "hover"
  | "border"
  | "icon"
  | "iconHover"
  | "iconDisabled"

type VariantColors = Partial<Record<VariantColor, number | string>>

type StylesParams = {
  iconOnly: boolean
  clickable: boolean
  isBodyClickable: boolean
  deleteIconPosition: ChipDeleteIconPosition
}

const FILLED_PARAMS: VariantColors = {
  bg: 600,
  text: "#FFFFFF",
  hover: 700,
  icon: 100,
  iconHover: 50,
  iconDisabled: 300
}
const OUTLINED_PARAMS: VariantColors = {
  bg: "transparent",
  hover: 50,
  border: 300,
  text: 800,
  icon: 400,
  iconHover: 500,
  iconDisabled: 100
}
const LIGHT_PARAMS: VariantColors = {
  bg: 50,
  text: 800,
  hover: 100,
  icon: 600,
  iconHover: 700,
  iconDisabled: 200
}
const LIGHT_STATUS_PARAMS: VariantColors = {
  bg: 100,
  text: 900,
  hover: 200,
  icon: 700,
  iconHover: 800,
  iconDisabled: 200
}

const getVariantStyles = (
  color: Color,
  { bg, text, hover, border, icon, iconHover, iconDisabled }: VariantColors,
  { clickable, iconOnly }: StylesParams
): CSSObject => ({
  backgroundColor: color[bg] ?? bg,
  color: color[text] ?? text,
  borderColor: color[border] ?? border,

  [`&.${chipClasses.focusVisible}`]: {
    backgroundColor: color[bg] ?? bg
  },

  [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}`]: {
    color: color[icon] ?? icon,

    "&:hover": {
      color: color[iconHover] ?? iconHover
    },

    [`&:disabled, &.${chipClasses.disabled}`]: {
      color: color[iconDisabled] ?? iconDisabled
    }
  },

  ...(iconOnly && {
    color: color[icon] ?? icon
  }),

  ...(clickable && {
    "&:hover": {
      backgroundColor: color[hover]
    }
  })
})

export const useStyles = makeStyles<StylesParams>()(
  ({ spacing, palette, typography, mixins }, params) => {
    const { iconOnly, isBodyClickable, deleteIconPosition } = params

    return {
      base: {
        width: "fit-content",
        height: "fit-content",
        border: `1px solid transparent`,
        borderRadius: "100px",
        ...(deleteIconPosition === "left" && { flexDirection: "row-reverse" }),

        // a11y: an invisible 24x24-min click/tap target, centered and expanded
        // to the chip when larger — wide chips grow only vertically, icon-only
        // chips (< 24px) also gain 24px width (SC 2.5.8)
        ...(isBodyClickable && {
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            height: "100%",
            minWidth: typography.pxToRem(24),
            minHeight: typography.pxToRem(24)
          }
        }),

        "&:focus": {
          ...mixins.focusRing.ring
        },

        [`&.${chipClasses.disabled}`]: {
          opacity: 0.6
        },

        [`& .${chipClasses.label}`]: {
          padding: spacing(0, 1),
          ...(iconOnly && { lineHeight: 0 })
        },

        [`& .${chipClasses.avatar}`]: {
          backgroundColor: indigo[500],
          color: palette.common.white,
          margin: "-1px 0px"
        },

        [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}`]: {
          fontSize: typography.pxToRem(11),
          lineHeight: typography.pxToRem(12),
          margin: "-1px 0px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        },

        // a11y: the delete affordance is an IconButton (focusable, keyboard/AT
        // operable). Strip its default padding + hover fill so it sits inside the
        // chip; the glyph size scales via the FaIcon size mapped to the chip size.
        [`& .${chipClasses.deleteIcon}`]: {
          padding: 0,
          position: "relative",
          "&:hover": {
            backgroundColor: "transparent"
          },
          [`& .${iconClasses.root}`]: {
            fontSize: typography.pxToRem(11),
            padding: 0
          },
          // a11y: an invisible 24x24 click/tap target centered on the small
          // remove glyph, without enlarging the visible icon (SC 2.5.8)
          "&::after": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            minWidth: typography.pxToRem(24),
            minHeight: typography.pxToRem(24)
          }
        },

        ...(iconOnly && {
          borderRadius: "50%",

          [`& .${chipClasses.label}`]: {
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }
        })
      },

      xs: {
        padding: "2px",
        fontSize: typography.pxToRem(12),
        lineHeight: typography.pxToRem(14),
        height: typography.pxToRem(18),

        [`& .${chipClasses.label}`]: {
          padding: spacing(0, 1)
        },

        [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}, & .${chipClasses.avatar}`]:
          {
            width: typography.pxToRem(16),
            height: typography.pxToRem(16),
            margin: "-1px 0px"
          },

        ...(iconOnly && {
          width: typography.pxToRem(16),
          height: typography.pxToRem(16),

          [`& .${chipClasses.label}`]: {
            padding: 0,

            [`& .${iconClasses.root}`]: {
              fontSize: typography.pxToRem(8),
              lineHeight: typography.pxToRem(8)
            }
          }
        })
      },
      sm: {
        padding: spacing(1),
        fontSize: typography.pxToRem(12),
        lineHeight: typography.pxToRem(14),
        height: typography.pxToRem(22),

        [`& .${chipClasses.label}`]: {
          padding: spacing(0, 1)
        },

        [`& .${chipClasses.icon}, & .${chipClasses.deleteIcon}, & .${chipClasses.avatar}`]:
          {
            width: typography.pxToRem(16),
            height: typography.pxToRem(16),
            margin: "-1px 0px"
          },

        ...(iconOnly && {
          width: typography.pxToRem(22),
          height: typography.pxToRem(22),

          [`& .${chipClasses.label}`]: {
            padding: 0,

            [`& .${iconClasses.root}`]: {
              fontSize: typography.pxToRem(12),
              lineHeight: typography.pxToRem(12)
            }
          }
        })
      },
      md: {
        padding: "5px 2px",
        fontSize: typography.pxToRem(14),
        lineHeight: typography.pxToRem(16),
        height: typography.pxToRem(26),

        [`& .${chipClasses.label}`]: {
          padding: "0px 6px"
        },

        [`& .${chipClasses.avatar}, & .${chipClasses.icon}, & .${chipClasses.deleteIcon}`]:
          {
            width: typography.pxToRem(22),
            height: typography.pxToRem(22),
            margin: "-3px 0px"
          },

        ...(iconOnly && {
          width: typography.pxToRem(26),
          height: typography.pxToRem(26),

          [`& .${chipClasses.label}`]: {
            padding: 0,

            [`& .${iconClasses.root}`]: {
              fontSize: typography.pxToRem(13),
              lineHeight: typography.pxToRem(13)
            }
          }
        })
      },
      lg: {
        padding: "5px 4px",
        fontSize: typography.pxToRem(16),
        lineHeight: typography.pxToRem(20),
        height: typography.pxToRem(30),

        [`& .${chipClasses.label}`]: {
          padding: spacing(0, 2)
        },

        [`& .${chipClasses.avatar}`]: {
          margin: "-3px 0px -3px -3px"
        },

        [`& .${chipClasses.avatar}, & .${chipClasses.icon}, & .${chipClasses.deleteIcon}`]:
          {
            width: typography.pxToRem(26),
            height: typography.pxToRem(26)
          },

        ...(iconOnly && {
          width: typography.pxToRem(36),
          height: typography.pxToRem(36),

          [`& .${chipClasses.label}`]: {
            padding: 0,

            [`& .${iconClasses.root}`]: {
              fontSize: typography.pxToRem(13),
              lineHeight: typography.pxToRem(13)
            }
          }
        })
      },

      filled_default: getVariantStyles(blueGrey, FILLED_PARAMS, params),
      filled_primary: getVariantStyles(lightBlue, FILLED_PARAMS, params),
      filled_secondary: getVariantStyles(teal, FILLED_PARAMS, params),
      filled_success: getVariantStyles(
        lightGreen,
        { ...FILLED_PARAMS, bg: 700, hover: 800 },
        params
      ),
      filled_warning: getVariantStyles(
        orange,
        { ...FILLED_PARAMS, bg: 700, hover: 800 },
        params
      ),
      filled_error: getVariantStyles(red, FILLED_PARAMS, params),
      filled_info: getVariantStyles(deepPurple, FILLED_PARAMS, params),

      outlined_default: getVariantStyles(blueGrey, OUTLINED_PARAMS, params),
      outlined_primary: getVariantStyles(
        lightBlue,
        { ...OUTLINED_PARAMS, icon: 500, iconHover: 600 },
        params
      ),
      outlined_secondary: getVariantStyles(
        teal,
        { ...OUTLINED_PARAMS, icon: 500, iconHover: 600 },
        params
      ),
      outlined_success: getVariantStyles(
        lightGreen,
        { ...OUTLINED_PARAMS, icon: 600, iconHover: 700 },
        params
      ),
      outlined_warning: getVariantStyles(
        orange,
        { ...OUTLINED_PARAMS, icon: 600, iconHover: 700 },
        params
      ),
      outlined_error: getVariantStyles(red, OUTLINED_PARAMS, params),
      outlined_info: getVariantStyles(deepPurple, OUTLINED_PARAMS, params),

      light_default: getVariantStyles(blueGrey, LIGHT_PARAMS, params),
      light_primary: getVariantStyles(lightBlue, LIGHT_PARAMS, params),
      light_secondary: getVariantStyles(
        teal,
        { ...LIGHT_PARAMS, icon: 500 },
        params
      ),
      light_success: getVariantStyles(lightGreen, LIGHT_STATUS_PARAMS, params),
      light_warning: getVariantStyles(orange, LIGHT_STATUS_PARAMS, params),
      light_error: getVariantStyles(
        red,
        { ...LIGHT_STATUS_PARAMS, icon: 600 },
        params
      ),
      light_info: getVariantStyles(
        deepPurple,
        { ...LIGHT_STATUS_PARAMS, icon: 600, iconHover: 700 },
        params
      ),

      skeleton: {
        width: typography.pxToRem(85),
        borderRadius: "100px"
      }
    }
  }
)
